import { FC, useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Button,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

// Preset types
export type DateRangePreset = 'thisMonth' | 'previousMonth' | 'last1Month' | 'last3Months' | 'last6Months' | 'last1Year' | 'currentFinYear' | 'previousFinYear' | 'custom';

// Helper to get financial year info
const getFinancialYearInfo = () => {
  const today = new Date();
  const currentMonth = today.getMonth(); // 0-11
  const currentYear = today.getFullYear();
  
  // Financial year starts on April 1st (month index 3)
  // If current month is Jan-Mar (0-2), we're in FY that started last year
  // If current month is Apr-Dec (3-11), we're in FY that started this year
  const currentFYStartYear = currentMonth < 3 ? currentYear - 1 : currentYear;
  const previousFYStartYear = currentFYStartYear - 1;
  
  return {
    currentFYStartYear,
    currentFYEndYear: currentFYStartYear + 1,
    previousFYStartYear,
    previousFYEndYear: previousFYStartYear + 1,
    currentFYLabel: `FY ${currentFYStartYear}-${(currentFYStartYear + 1).toString().slice(-2)}`,
    previousFYLabel: `FY ${previousFYStartYear}-${(previousFYStartYear + 1).toString().slice(-2)}`,
  };
};

// Get dynamic financial year labels
const fyInfo = getFinancialYearInfo();

// Preset configuration
const presetOptions: { value: DateRangePreset; label: string; months: number }[] = [
  { value: 'thisMonth', label: 'This Month', months: 1 },
  { value: 'previousMonth', label: 'Previous Month', months: 1 },
  { value: 'last1Month', label: 'Last 1 Month', months: 1 },
  { value: 'last3Months', label: 'Last 3 Months', months: 3 },
  { value: 'last6Months', label: 'Last 6 Months', months: 6 },
  { value: 'last1Year', label: 'Last 1 Year', months: 12 },
  { value: 'currentFinYear', label: fyInfo.currentFYLabel, months: 12 },
  { value: 'previousFinYear', label: fyInfo.previousFYLabel, months: 12 },
  { value: 'custom', label: 'Custom', months: 0 },
];

// Calculate dates based on preset
const calculateDatesFromPreset = (preset: DateRangePreset): { start: Date; end: Date } => {
  const today = new Date();
  let start: Date;
  let end: Date = new Date(today);
  const fy = getFinancialYearInfo();

  switch (preset) {
    case 'thisMonth':
      // From 1st of current month to today
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
      break;
    case 'previousMonth':
      // From 1st to last day of previous month
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0); // Last day of previous month
      break;
    case 'last1Month':
      start = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
      break;
    case 'last3Months':
      start = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
      break;
    case 'last6Months':
      start = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
      break;
    case 'last1Year':
      start = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
      break;
    case 'currentFinYear':
      // Current FY: April 1st of start year to March 31st of end year (or today if FY not complete)
      start = new Date(fy.currentFYStartYear, 3, 1); // April 1st
      end = new Date(today); // Up to today
      break;
    case 'previousFinYear':
      // Previous FY: April 1st to March 31st
      start = new Date(fy.previousFYStartYear, 3, 1); // April 1st
      end = new Date(fy.previousFYEndYear, 2, 31); // March 31st
      break;
    case 'custom':
    default:
      // Default to last 3 months for custom initial state
      start = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
      break;
  }

  return { start, end };
};

// Calculate months between two dates
const getMonthsDifference = (startDate: Date, endDate: Date): number => {
  const months =
    (endDate.getFullYear() - startDate.getFullYear()) * 12 +
    (endDate.getMonth() - startDate.getMonth());
  // Add partial month if end day is after start day
  const dayDiff = endDate.getDate() - startDate.getDate();
  return dayDiff >= 0 ? months : months - 1;
};

export interface DateRangeSelectorProps {
  /** Called when date range changes (on preset select or "View" click for custom) */
  onChange: (startDate: Date, endDate: Date, preset: DateRangePreset) => void;
  /** Initial preset selection (defaults to 'last3Months') */
  defaultPreset?: DateRangePreset;
  /** Show/hide the View button (defaults to true) */
  showViewButton?: boolean;
  /** Custom label for View button (defaults to 'View') */
  viewButtonLabel?: string;
  /** Max selectable date (defaults to today) */
  maxDate?: Date;
  /** Whether to trigger onChange on initial mount (defaults to true) */
  triggerOnMount?: boolean;
  /** Maximum allowed date range in months (defaults to 24) */
  maxRangePeriodMonths?: number;
  /** Called when range exceeds maxRangePeriodMonths */
  onRangeError?: (message: string) => void;
  /** Initial preset from URL (overrides defaultPreset if valid) */
  initialPreset?: DateRangePreset;
  /** Initial start date from URL (used when initialPreset is 'custom') */
  initialStartDate?: Date;
  /** Initial end date from URL (used when initialPreset is 'custom') */
  initialEndDate?: Date;
  excludePresets?: DateRangePreset[];
}

export const DateRangeSelector: FC<DateRangeSelectorProps> = ({
  onChange,
  defaultPreset = 'last3Months',
  showViewButton = true,
  viewButtonLabel = 'View',
  maxDate,
  triggerOnMount = true,
  maxRangePeriodMonths = 24,
  onRangeError,
  initialPreset,
  initialStartDate,
  initialEndDate,
  excludePresets,
}) => {
  const today = new Date();
  const effectiveMaxDate = maxDate || today;

  // Determine effective preset - use initialPreset if valid, otherwise defaultPreset
  const effectivePreset = initialPreset && presetOptions.some(p => p.value === initialPreset) 
    ? initialPreset 
    : defaultPreset;

  // State
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>(effectivePreset);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [rangeError, setRangeError] = useState<string | null>(null);

  // Filter preset options based on maxRangePeriodMonths, then optional exclusions
  const availablePresets = presetOptions
    .filter(
      (option) => option.value === 'custom' || option.months <= maxRangePeriodMonths
    )
    .filter(
      (option) =>
        !excludePresets?.length || !excludePresets.includes(option.value)
    );

  // Validate date range
  const validateRange = (start: Date, end: Date): boolean => {
    const monthsDiff = getMonthsDifference(start, end);
    if (monthsDiff > maxRangePeriodMonths) {
      const errorMsg = `Date range cannot exceed ${maxRangePeriodMonths} months`;
      setRangeError(errorMsg);
      if (onRangeError) {
        onRangeError(errorMsg);
      }
      return false;
    }
    setRangeError(null);
    return true;
  };

  // Initialize dates on mount
  useEffect(() => {
    let start: Date;
    let end: Date;

    // If initial dates are provided (from URL), use them for custom preset
    if (effectivePreset === 'custom' && initialStartDate && initialEndDate) {
      start = initialStartDate;
      end = initialEndDate;
    } else {
      // Calculate dates from the effective preset
      const calculated = calculateDatesFromPreset(effectivePreset);
      start = calculated.start;
      end = calculated.end;
    }

    setStartDate(start);
    setEndDate(end);
    setIsInitialized(true);

    // Trigger onChange on mount if enabled
    if (triggerOnMount) {
      onChange(start, end, effectivePreset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle preset change
  const handlePresetChange = (event: SelectChangeEvent<DateRangePreset>) => {
    const newPreset = event.target.value as DateRangePreset;
    setSelectedPreset(newPreset);
    setRangeError(null);

    if (newPreset !== 'custom') {
      const { start, end } = calculateDatesFromPreset(newPreset);
      setStartDate(start);
      setEndDate(end);

      // Immediately trigger onChange for presets
      onChange(start, end, newPreset);
    }
  };

  // Handle start date change
  const handleStartDateChange = (value: Date | null) => {
    if (value) {
      setStartDate(value);
      // Ensure start date is not after end date
      let effectiveEndDate = endDate;
      if (endDate && value.getTime() > endDate.getTime()) {
        setEndDate(value);
        effectiveEndDate = value;
      }

      if (effectiveEndDate) {
        // Validate range - always validate to update error state
        const isValid = validateRange(value, effectiveEndDate);

        // Auto-trigger if no view button and valid
        if (!showViewButton && isValid) {
          onChange(value, effectiveEndDate, 'custom');
        }
      }
    }
  };

  // Handle end date change
  const handleEndDateChange = (value: Date | null) => {
    if (value) {
      setEndDate(value);
      // Ensure end date is not before start date
      let effectiveStartDate = startDate;
      if (startDate && value.getTime() < startDate.getTime()) {
        setStartDate(value);
        effectiveStartDate = value;
      }

      if (effectiveStartDate) {
        // Validate range - always validate to update error state
        const isValid = validateRange(effectiveStartDate, value);

        // Auto-trigger if no view button and valid
        if (!showViewButton && isValid) {
          onChange(effectiveStartDate, value, 'custom');
        }
      }
    }
  };

  // Handle View button click
  const handleViewClick = () => {
    if (startDate && endDate) {
      if (validateRange(startDate, endDate)) {
        onChange(startDate, endDate, selectedPreset);
      }
    }
  };

  const isCustom = selectedPreset === 'custom';

  if (!isInitialized) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 0.5
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'nowrap',
        }}
      >
        {/* Preset Dropdown */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={selectedPreset}
            onChange={handlePresetChange}
            sx={{
              fontSize: '0.875rem',
              '& .MuiSelect-select': {
                py: 1,
              },
            }}
          >
            {availablePresets.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date Pickers (shown only for Custom) */}
        {isCustom && (
          <>
            <DatePicker
              value={startDate}
              onChange={handleStartDateChange}
              label="Start"
              format="dd/MM/yyyy"
              maxDate={effectiveMaxDate}
              slotProps={{
                textField: {
                  size: 'small',
                  error: !!rangeError,
                  sx: {
                    width: 145,
                    '& .MuiInputBase-input': {
                      fontSize: '0.875rem',
                      py: 1,
                    },
                  },
                },
              }}
            />
            <DatePicker
              value={endDate}
              onChange={handleEndDateChange}
              label="End"
              format="dd/MM/yyyy"
              minDate={startDate || undefined}
              maxDate={effectiveMaxDate}
              slotProps={{
                textField: {
                  size: 'small',
                  error: !!rangeError,
                  sx: {
                    width: 145,
                    '& .MuiInputBase-input': {
                      fontSize: '0.875rem',
                      py: 1,
                    },
                  },
                },
              }}
            />
          </>
        )}

        {/* View Button */}
        {showViewButton && (
          <Button
            variant="contained"
            onClick={handleViewClick}
            disabled={!!rangeError}
            sx={{
              backgroundColor: '#37A239',
              height: 40,
              minWidth: 60,
              fontSize: '0.875rem',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#2d8a2f',
              },
            }}
          >
            {viewButtonLabel}
          </Button>
        )}
      </Box>

      {/* Error Message or Date Range Display */}
        <Typography
          variant="caption"
          sx={{
            color: rangeError && isCustom ? 'error.main' : 'primary.main',
            fontSize: '0.70rem',
          }}
        >
          {rangeError && isCustom 
            ? rangeError 
            : (!isCustom && startDate && endDate)
              ? `${startDate.toLocaleDateString('en-GB')} - ${endDate.toLocaleDateString('en-GB')}`
              : '\u00A0'}
        </Typography>
    
    </Box>
  );
};

export default DateRangeSelector;
